import { useEffect, useState, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Search,
  Bell,
  Package,
  ClipboardList,
  Wrench,
  AlertTriangle,
  Cpu,
  FileCheck,
  Info,
  Check,
} from "lucide-react";
import { api } from "../api";
import type { CfAccessIdentity } from "../types";

// ---------------------------------------------------------------------------
// Generic fetch helper for non-v1 APIs
// ---------------------------------------------------------------------------

async function rawFetch<T>(url: string): Promise<T> {
  const res = await fetch(url, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

// ---------------------------------------------------------------------------
// Debounce hook
// ---------------------------------------------------------------------------

function useDebounce(value: string, ms: number) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

// ---------------------------------------------------------------------------
// Types for search results
// ---------------------------------------------------------------------------

interface WorkOrder {
  id: number;
  product_name: string;
  product_sku: string | null;
  status: string;
  priority?: string;
  scheduled_end?: string;
  created_at?: string;
}

interface CrmOrder {
  id: number;
  order_no: string;
  status: string;
  created_at: string;
}

interface Equipment {
  id: number;
  name: string;
  type: string;
  status: string;
}

interface SearchResult {
  category: string;
  label: string;
  sub: string;
  to: string;
  icon: React.ElementType;
}

// ---------------------------------------------------------------------------
// Types for notifications
// ---------------------------------------------------------------------------

interface InventoryAlert {
  id: number;
  type: string;
  message: string;
  severity: string;
  acknowledged: number;
  created_at: string;
}

interface Prediction {
  id: number;
  equipment_id: number;
  equipment_name?: string;
  failure_type: string;
  probability: number;
  predicted_date: string;
  created_at: string;
}

interface Notification {
  id: string;
  type: "inventory" | "equipment" | "approval" | "system";
  message: string;
  timestamp: string;
  link: string;
}

const NOTIF_STYLES: Record<
  Notification["type"],
  { icon: React.ElementType; color: string; bg: string }
> = {
  inventory: { icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-50" },
  equipment: { icon: Cpu, color: "text-red-600", bg: "bg-red-50" },
  approval: { icon: FileCheck, color: "text-blue-600", bg: "bg-blue-50" },
  system: { icon: Info, color: "text-slate-500", bg: "bg-slate-50" },
};

const READ_KEY = "qimei_read_notifications";

function getReadIds(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(READ_KEY) || "[]"));
  } catch {
    return new Set();
  }
}

function persistReadIds(ids: Set<string>) {
  localStorage.setItem(READ_KEY, JSON.stringify([...ids]));
}

// ---------------------------------------------------------------------------
// Global Search component
// ---------------------------------------------------------------------------

function GlobalSearch() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debouncedQuery = useDebounce(query, 300);

  const hasQuery = debouncedQuery.trim().length > 0;

  const products = useQuery({
    queryKey: ["search-products", debouncedQuery],
    queryFn: () => api.listProducts({ search: debouncedQuery, per_page: 5 }),
    enabled: hasQuery,
  });

  const orders = useQuery({
    queryKey: ["search-orders", debouncedQuery],
    queryFn: () => rawFetch<WorkOrder[]>("/api/production/orders"),
    enabled: hasQuery,
  });

  const equipment = useQuery({
    queryKey: ["search-equipment", debouncedQuery],
    queryFn: () => rawFetch<Equipment[]>("/api/maintenance/equipment"),
    enabled: hasQuery,
  });

  const results: SearchResult[] = [];

  if (hasQuery) {
    const q = debouncedQuery.toLowerCase();

    if (products.data?.items) {
      for (const p of products.data.items.slice(0, 5)) {
        results.push({
          category: "產品",
          label: p.name,
          sub: p.sku || p.category || "",
          to: `/products/${p.id}`,
          icon: Package,
        });
      }
    }

    if (orders.data) {
      const matched = orders.data
        .filter(
          (o) =>
            o.product_name.toLowerCase().includes(q) ||
            (o.product_sku && o.product_sku.toLowerCase().includes(q)) ||
            String(o.id).includes(q),
        )
        .slice(0, 5);
      for (const o of matched) {
        results.push({
          category: "工單",
          label: `#${o.id} ${o.product_name}`,
          sub: o.status,
          to: "/production",
          icon: ClipboardList,
        });
      }
    }

    if (equipment.data) {
      const matched = equipment.data
        .filter(
          (e) =>
            e.name.toLowerCase().includes(q) ||
            e.type.toLowerCase().includes(q),
        )
        .slice(0, 5);
      for (const e of matched) {
        results.push({
          category: "設備",
          label: e.name,
          sub: `${e.type} · ${e.status}`,
          to: "/maintenance",
          icon: Wrench,
        });
      }
    }
  }

  // Cmd+K shortcut
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === "Escape") {
        setOpen(false);
        inputRef.current?.blur();
      }
    }
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const showDropdown = open && hasQuery;

  // Group results by category
  const grouped = results.reduce<Record<string, SearchResult[]>>((acc, r) => {
    (acc[r.category] ??= []).push(r);
    return acc;
  }, {});

  return (
    <div ref={containerRef} className="relative flex-1 max-w-md mx-4">
      <div className="relative">
        <Search
          size={15}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
        />
        <input
          ref={inputRef}
          type="text"
          placeholder="搜尋產品、工單、設備..."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          className="w-full h-8 pl-9 pr-16 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-700 placeholder:text-slate-400 outline-none transition-colors focus:border-qimei-300 focus:bg-white focus:ring-2 focus:ring-qimei-100"
        />
        <kbd className="absolute right-3 top-1/2 -translate-y-1/2 hidden sm:inline-flex items-center gap-0.5 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-slate-400">
          ⌘K
        </kbd>
      </div>

      {showDropdown && (
        <div className="absolute top-full left-0 right-0 mt-1.5 rounded-xl border border-slate-200 bg-white shadow-lg z-50 overflow-hidden">
          <div className="max-h-[400px] overflow-y-auto">
            {results.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-slate-400">
                {products.isLoading || orders.isLoading || equipment.isLoading
                  ? "搜尋中..."
                  : `找不到「${debouncedQuery}」的結果`}
              </div>
            ) : (
              Object.entries(grouped).map(([cat, items]) => (
                <div key={cat}>
                  <div className="px-3 py-1.5 text-[10px] font-semibold tracking-wider text-slate-400 uppercase bg-slate-50 border-b border-slate-100">
                    {cat}
                  </div>
                  {items.map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={`${cat}-${i}`}
                        className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-slate-50 transition-colors"
                        onClick={() => {
                          navigate(item.to);
                          setOpen(false);
                          setQuery("");
                        }}
                      >
                        <Icon size={16} className="text-slate-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-700 truncate">
                            {item.label}
                          </p>
                          {item.sub && (
                            <p className="text-[11px] text-slate-400 truncate">
                              {item.sub}
                            </p>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Notification Bell component
// ---------------------------------------------------------------------------

function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [readIds, setReadIds] = useState<Set<string>>(getReadIds);
  const containerRef = useRef<HTMLDivElement>(null);

  const alertsQuery = useQuery({
    queryKey: ["notif-inventory-alerts"],
    queryFn: () => rawFetch<InventoryAlert[]>("/api/inventory/alerts"),
    refetchInterval: 60_000,
  });

  const predictionsQuery = useQuery({
    queryKey: ["notif-maintenance-predictions"],
    queryFn: () => rawFetch<Prediction[]>("/api/maintenance/predictions"),
    refetchInterval: 60_000,
  });

  const prodOrdersQuery = useQuery({
    queryKey: ["notif-prod-orders"],
    queryFn: () => rawFetch<WorkOrder[] | { items: WorkOrder[] }>("/api/production/orders"),
    refetchInterval: 60_000,
  });

  const crmOrdersQuery = useQuery({
    queryKey: ["notif-crm-orders"],
    queryFn: () => rawFetch<CrmOrder[] | { items: CrmOrder[] }>("/api/crm/orders"),
    refetchInterval: 60_000,
  });

  const notifications: Notification[] = [];

  if (alertsQuery.data) {
    for (const a of alertsQuery.data.filter((a) => !a.acknowledged)) {
      notifications.push({
        id: `inv-${a.id}`,
        type: "inventory",
        message: a.message,
        timestamp: a.created_at,
        link: "/inventory",
      });
    }
  }

  if (predictionsQuery.data) {
    for (const p of predictionsQuery.data.filter((p) => p.probability >= 0.7)) {
      notifications.push({
        id: `maint-${p.id}`,
        type: "equipment",
        message: `${p.equipment_name || `設備 #${p.equipment_id}`} — ${p.failure_type}（機率 ${Math.round(p.probability * 100)}%）`,
        timestamp: p.created_at,
        link: "/maintenance",
      });
    }
  }

  if (prodOrdersQuery.data) {
    const now = Date.now();
    const prodOrders = Array.isArray(prodOrdersQuery.data) ? prodOrdersQuery.data : prodOrdersQuery.data.items ?? [];
    for (const o of prodOrders) {
      // Urgent pending orders
      if (o.priority === "urgent" && o.status === "pending") {
        notifications.push({
          id: `prod-urgent-${o.id}`,
          type: "approval",
          message: `[急] 工單 #${o.id} ${o.product_name} 待排程`,
          timestamp: o.created_at || new Date().toISOString(),
          link: "/production",
        });
      }
      // Overdue orders (scheduled_end in the past but not completed)
      if (
        o.scheduled_end &&
        o.status !== "completed" &&
        new Date(o.scheduled_end).getTime() < now
      ) {
        notifications.push({
          id: `prod-overdue-${o.id}`,
          type: "system",
          message: `工單 #${o.id} 已逾期`,
          timestamp: o.scheduled_end,
          link: "/production",
        });
      }
    }
  }

  if (crmOrdersQuery.data) {
    const twoDaysAgo = Date.now() - 2 * 24 * 60 * 60 * 1000;
    const crmOrders = Array.isArray(crmOrdersQuery.data) ? crmOrdersQuery.data : crmOrdersQuery.data.items ?? [];
    for (const o of crmOrders) {
      if (o.status === "pending" && new Date(o.created_at).getTime() < twoDaysAgo) {
        notifications.push({
          id: `crm-${o.id}`,
          type: "approval",
          message: `客戶訂單 ${o.order_no} 待確認`,
          timestamp: o.created_at,
          link: "/crm",
        });
      }
    }
  }

  notifications.sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  );

  const unreadCount = notifications.filter((n) => !readIds.has(n.id)).length;

  const markAllRead = useCallback(() => {
    const next = new Set(readIds);
    for (const n of notifications) next.add(n.id);
    setReadIds(next);
    persistReadIds(next);
  }, [notifications, readIds]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  function formatRelative(ts: string) {
    const diff = Date.now() - new Date(ts).getTime();
    const mins = Math.floor(diff / 60_000);
    if (mins < 1) return "剛剛";
    if (mins < 60) return `${mins} 分鐘前`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} 小時前`;
    return `${Math.floor(hrs / 24)} 天前`;
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative flex items-center justify-center w-8 h-8 rounded-lg hover:bg-slate-100 transition-colors"
      >
        <Bell size={18} className="text-slate-500" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-qimei-500 px-1 text-[10px] font-bold text-white">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-80 rounded-xl border border-slate-200 bg-white shadow-lg z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
            <span className="text-sm font-semibold text-slate-700">通知</span>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="flex items-center gap-1 text-[11px] text-qimei-600 hover:text-qimei-700 transition-colors"
              >
                <Check size={12} />
                全部標記已讀
              </button>
            )}
          </div>

          <div className="max-h-[400px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-400">
                沒有新通知
              </div>
            ) : (
              notifications.map((n) => {
                const style = NOTIF_STYLES[n.type];
                const Icon = style.icon;
                const isRead = readIds.has(n.id);
                return (
                  <Link
                    key={n.id}
                    to={n.link}
                    onClick={() => {
                      const next = new Set(readIds);
                      next.add(n.id);
                      setReadIds(next);
                      persistReadIds(next);
                      setOpen(false);
                    }}
                    className={`flex items-start gap-3 px-4 py-3 no-underline hover:bg-slate-50 transition-colors border-b border-slate-50 ${isRead ? "opacity-60" : ""}`}
                  >
                    <div
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${style.bg}`}
                    >
                      <Icon size={15} className={style.color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] text-slate-700 leading-snug">
                        {n.message}
                      </p>
                      <p className="mt-0.5 text-[11px] text-slate-400">
                        {formatRelative(n.timestamp)}
                      </p>
                    </div>
                    {!isRead && (
                      <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-qimei-500" />
                    )}
                  </Link>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TopBar
// ---------------------------------------------------------------------------

export function TopBar() {
  const [identity, setIdentity] = useState<CfAccessIdentity | null>(null);

  useEffect(() => {
    api.getIdentity().then(setIdentity);
  }, []);

  return (
    <header className="h-14 border-b border-slate-200 bg-white/95 backdrop-blur-sm flex items-center px-6 shrink-0 sticky top-0 z-40">
      <Link to="/" className="flex items-center gap-3 text-slate-900 no-underline">
        <div className="w-8 h-8 rounded-lg bg-qimei-500 flex items-center justify-center text-white font-bold text-sm">
          Q
        </div>
        <span className="font-semibold text-base tracking-tight">
          奇美食品
        </span>
        <span className="text-xs text-slate-400 hidden sm:inline">
          數位管理平台
        </span>
      </Link>
      <GlobalSearch />
      <div className="flex items-center gap-3">
        <NotificationBell />
        {identity?.email && (
          <span className="text-xs text-slate-500">{identity.email}</span>
        )}
      </div>
    </header>
  );
}
